import React, { useMemo } from "react";
import { MONTH_NAMES } from "../constants";
import { formatCurrency } from "../utils/helpers";
import { useMonthlyData } from "../hooks/useMonthlyData";
import { useGroupBudgets } from "../hooks/useGroupBudgets";
import { useMonth } from "../context/MonthContext";
import SpendingChart from "./SpendingChart";
import CategorySummary from "./CategorySummary";
import MonthlyExpensesCalendar from "./MonthlyExpensesCalendar";

interface MonthlyDashboardProps {
  refreshTrigger?: number;
  onBudgetChanged?: () => void;
}

const MonthlyDashboard: React.FC<MonthlyDashboardProps> = ({ refreshTrigger, onBudgetChanged }) => {
  const {
    selectedMonth,
    currentYear,
    transactions,
    loading: transactionsLoading,
    error: transactionsError,
    refreshTransactions
  } = useMonth();
  const { summary, loading, refresh } = useMonthlyData(selectedMonth);
  const { budgetSummary, loading: budgetsLoading, refreshBudgets } = useGroupBudgets(refreshTrigger);

  const totalIncome = Number(summary?.total_income) || 0;
  const totalExpenses = Math.abs(Number(summary?.total_expenses) || 0);
  const netBalance = totalIncome - totalExpenses;

  // Refresh when trigger changes
  React.useEffect(() => {
    if (refreshTrigger) {
      refresh();
      refreshBudgets();
      refreshTransactions();
    }
  }, [refreshTrigger, refresh, refreshBudgets, refreshTransactions]);

  const categoryBudget = useMemo(() => {
    const budgetMap: { [key: string]: number } = {};
    budgetSummary?.category_groups?.forEach((group) => {
      group.categories.forEach((budget) => {
        budgetMap[budget.category] = Number(budget.category_budget) || 0;
      });
    });
    return budgetMap;
  }, [budgetSummary]);

  // 使用 API 数据，失败时显示空数据
  const categorySpending = useMemo(() => {
    if (summary?.category_breakdown) {
      return Object.fromEntries(
        Object.entries(summary.category_breakdown).map(([key, value]) => [
          key,
          Math.abs(value),
        ])
      );
    }
    return {};
  }, [summary]);

  // Calculate remaining budget using the same logic as CategorySummary
  const remainingBudget = useMemo(() => {
    let totalBudgetSum = 0;
    let totalActualSum = 0;

    Object.entries(categoryBudget).forEach(([category, budget]: [string, number]) => {
      const actual = categorySpending[category] || 0;
      totalBudgetSum += budget;
      totalActualSum += actual;
    });

    return totalBudgetSum - totalActualSum;
  }, [categoryBudget, categorySpending]);

  return (
    <section className="mb-12 bg-white border border-gray-200 rounded-xl shadow-lg p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6">
        <h2 className="text-2xl font-semibold">月度仪表板</h2>
      </div>

      {(loading || transactionsLoading || budgetsLoading) && (
        <div className="text-center py-4">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-slate-700 mx-auto mb-2"></div>
          <p className="text-gray-600">加载月度数据中...</p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8 text-center">
        <div className="bg-gray-100 p-4 rounded-lg">
          <h3 className="text-sm font-medium text-gray-500">总收入</h3>
          <p className="text-3xl font-semibold text-green-700">
          {formatCurrency(totalIncome)}
          </p>
        </div>
        <div className="bg-gray-100 p-4 rounded-lg">
          <h3 className="text-sm font-medium text-gray-500">总支出</h3>
          <p className="text-3xl font-semibold text-red-600">
          {formatCurrency(totalExpenses)}
          </p>
        </div>
        <div className="bg-gray-100 p-4 rounded-lg">
          <h3 className="text-sm font-medium text-gray-500">剩余预算</h3>
          <p className={`text-3xl font-semibold ${remainingBudget >= 0 ? 'text-green-600' : 'text-red-600'}`}>
          {formatCurrency(remainingBudget)}
          </p>
        </div>
        <div className="bg-gray-100 p-4 rounded-lg">
          <h3 className="text-sm font-medium text-gray-500">净结余</h3>
          <p className={`text-3xl font-semibold ${netBalance >= 0 ? 'text-green-700' : 'text-red-600'}`}>
          {formatCurrency(netBalance)}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
        <div className="lg:col-span-2 space-y-6">
          {/* 按类别支出图表 */}
          <div>
            <h3 className="text-xl font-semibold mb-4 text-center">按类别支出</h3>
            <p className="text-gray-600 text-center mb-4">
              此图表显示您本月的资金去向。使用此可视化图表快速识别最大的支出领域。
            </p>
            <SpendingChart categorySpending={categorySpending} />
          </div>
          
          {/* 每日支出日历 */}
          <div>
            <MonthlyExpensesCalendar 
              transactions={transactions}
              selectedMonth={selectedMonth}
              currentYear={currentYear}
              loading={transactionsLoading}
              error={transactionsError}
            />
          </div>
        </div>
        
        <div className="lg:col-span-3">
          <h3 className="text-xl font-semibold mb-4">类别深度分析</h3>
          <p className="text-gray-600 mb-4">
            这里详细比较了您每个类别的计划预算与实际支出。您可以在此分析表现并找到优化机会。
          </p>
          <CategorySummary
            categorySpending={categorySpending}
            categoryBudget={categoryBudget}
            budgetSummary={budgetSummary}
            onBudgetChanged={() => {
              refreshBudgets();
              onBudgetChanged?.();
            }}
          />
        </div>
      </div>
    </section>
  );
};

export default MonthlyDashboard;
